import { addDays } from "date-fns"
import { useMemo } from "react"
import { useChannelForecast } from "~/hooks/useForecasts"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"

export const useVariantChannelData = (
  variants: IVariantSummary[],
  channel: string | undefined,
  warehouseId: string | undefined,
  enabled: boolean,
) => {
  let isLoading = false
  let isFetching = false

  const fiftyTwoWeeksFromNow = addDays(getStartOfCurrentWeekUTC(), 52 * 7)
  const thirteenWeeksFromNow = addDays(getStartOfCurrentWeekUTC(), 91)

  const {
    forecasts,
    getForecastUntilDay,
    isLoading: forecastIsLoading,
    isFetching: forecastIsFetching,
  } = useChannelForecast(channel, warehouseId, enabled)
  isLoading ||= forecastIsLoading
  isFetching ||= forecastIsFetching

  // Cached per variant object, so a page that narrows `variants` (a search keystroke) keeps each
  // row's object and AG Grid diffs the row set instead of updating every row.
  const toChannelRow = useMemo(() => {
    const rowByVariant = new WeakMap<IVariantSummary, IVariantSummary>()
    return (variant: IVariantSummary) => {
      const cached = rowByVariant.get(variant)
      if (cached) return cached

      const forecastQuantity = getForecastUntilDay(fiftyTwoWeeksFromNow, variant.variantId)
      const thirteenWeeksForecast = getForecastUntilDay(thirteenWeeksFromNow, variant.variantId)

      const row = {
        ...variant,
        variantForecast: forecasts?.get(variant.variantId) ?? [],
        thirteenWeeksForecast,
        forecastQuantity,
        // Stock walks need every channel, so only this table's rows are narrowed.
        futureKnownPreorders: variant.futureKnownPreorders?.filter((po) => po.channel === channel),
      }
      rowByVariant.set(variant, row)
      return row
    }
  }, [warehouseId, channel, forecasts])

  const data = useMemo(() => {
    if (!variants || !forecasts) {
      return []
    }

    return (variants ?? []).map(toChannelRow)
  }, [variants, forecasts, toChannelRow])

  return { data, isLoading, isFetching }
}
